import React, { useEffect, useState } from "react";
import "./panel.scss";
import {
  Col,
  Divider,
  Dropdown,
  Grid,
  Layout,
  Row,
  Tag,
  Typography,
} from "antd";
import Moment from "jalali-moment";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Dashboard from "../user/dashboard/dashboard";
import Report from "../user/report/report";
import Export from "../user/export/export";
import Profile from "../user/profile/profile";
import Icon from "../../components/icon/icon";
import { AdminMenu, UserMenu } from "../../components/menu/menu";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { appSlice } from "../../redux/slices/app";
import OrganizationProfile from "../admin/organization/profile/profile";
import OrganizationSetting from "../admin/organization/settings/settings";
import OrganizationRole from "../admin/organization/roles/roles";
import Users from "../admin/organization/users/users";
import { HandleErrors } from "../../utils/handleErrors";
import { getMeService, logoutService } from "../../services/user.service";
import { userSlice, UserState } from "../../redux/slices/user";
import {
  getPermissionsOfRoleService,
  getRolesService,
} from "../../services/role.service";
import { GetPermissionRes, GetRoleRes } from "../../interfaces/role.interface";
import LogSetting from "../admin/logSetting/logSetting";
import Risk from "../admin/organization/risk/risk";
import { Application } from "../admin/application/application";
import { adminMenuData } from "../../config/menu";
import { AlertRule } from "../admin/alert/alertRule/alertRule";
import { AlertReceiver } from "../admin/alert/alertReceiver/alertReceiver";
import Gravatar from "react-gravatar";
import { getSystemHealthService } from "../../services/health.service";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { PermissionCode } from "../../enum/permission";

const { Header, Content, Footer, Sider } = Layout;
const { useBreakpoint } = Grid;

const Panel = () => {
  const screens = useBreakpoint();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const currentSpace = useSelector<RootState, string>(
    (root) => root.app.currentSpace
  );
  const user = useSelector<RootState, UserState>((root) => root.user);

  const [roles, setRoles] = useState<GetRoleRes[]>([]);
  const [adminAccess, setAdminAccess] = useState(false);

  const [systemHealthStatus, setSystemHealthStatus] = useState('false');

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/auth/login", { replace: true });
    } else {
      getRoles();
      getMe();
    }

    const isInAdmin = location.pathname.indexOf("admin");
    if (isInAdmin != -1) {
      dispatch(appSlice.actions.setCurrentSpace("admin"));
    } else {
      dispatch(appSlice.actions.setCurrentSpace("user"));
    }
  }, []);

  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDate(Moment().format("jYYYY/jMM/jDD"));
      setTime(Moment().format("HH:mm:ss"));
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (
      user.permissions.find((p) => p.code === PermissionCode.getSystemHealth)
    ) {
      getSystemHealth();
      const interval = setInterval(() => {
        getSystemHealth();
      }, 1000 * 10);

      return () => {
        clearInterval(interval);
      };
    }
  }, [user]);

  useEffect(() => {
    if (roles.length > 0) {
      let temp = "";
      user.roles.map((roleId) => {
        const role = roles.find((e) => e.id == roleId);

        if (role && temp.length == 0) {
          temp += role.name;
        } else if (role && temp.length > 0) {
          temp += `,${role.name}`;
        }
      });

      dispatch(userSlice.actions.setRoleName(temp));
    }
  }, [user.roles, roles]);

  const getMe = async () => {
    try {
      const res = await getMeService({});
      dispatch(userSlice.actions.setUser(res));

      let userPermissions: GetPermissionRes[] = [];

      for (const id of res.roles) {
        const permissions = await getPermissionsOfRoleService({ role_id: id });
        permissions.list.map((i) => {
          if (!userPermissions.find((e) => e.code === i.code)) {
            userPermissions.push(i);
          }
        });
      }

      dispatch(userSlice.actions.setPermissions(userPermissions));

      // check user can access to admin panel
      adminMenuData.map((item) => {
        const hasPermission = userPermissions.some((item1) =>
          item.permissions.some((item2) => item1.code === item2)
        );

        if (hasPermission) {
          if (!adminAccess) {
            setAdminAccess(true);
          }
        }
      });
    } catch (error) {
      HandleErrors(error);
    }
  };

  const getSystemHealth = async () => {
    try {
      const res = await getSystemHealthService();
      if(res.license <= 0){
        setSystemHealthStatus('license-false');
        dispatch(appSlice.actions.setHealth('license-false'));
      }else if (
        res.core &&
        res.event_alert &&
        res.event_collector &&
        res.event_export &&
        res.event_management &&
        res.event_validator
      ) {
        setSystemHealthStatus('true');
        dispatch(appSlice.actions.setHealth('true'));
      } else {
        setSystemHealthStatus('false');
        dispatch(appSlice.actions.setHealth('false'));
      }
    } catch (error) {
      setSystemHealthStatus('false');
      dispatch(appSlice.actions.setHealth('false'));
    }
  };

  const getRoles = async () => {
    try {
      const res = await getRolesService({});
      setRoles(res.list);
    } catch (error) {
      HandleErrors(error);
    }
  };

  const logout = async () => {
    try {
      await logoutService({})
      localStorage.clear();
      navigate("/auth/login", { replace: true });
    } catch (error) {
      localStorage.clear();
      navigate("/auth/login", { replace: true });
    }
  };

  return (
    <div className="Panel">
      <Layout style={{ minHeight: "100vh" }}>
        <Sider
          breakpoint="lg"
          collapsedWidth="0"
          onCollapse={(e) => {
            setIsCollapsed(e);
          }}
          collapsed={isCollapsed}
          width={250}
          style={{
            backgroundColor: "#fff",
          }}
        >
          <div className="menu-wrapper">
            <div className="menu">
              {currentSpace === "user" && (
                <UserMenu setIsCollapsed={setIsCollapsed} />
              )}
              {currentSpace === "admin" && (
                <AdminMenu setIsCollapsed={setIsCollapsed} />
              )}
            </div>
            <div>
              {screens.lg && (
                <div className="user-info-box">
                  <Row justify={"space-between"}>
                    <Col className="name-box">
                      <Typography.Text className="name">
                        {user.first_name} {user.last_name}
                      </Typography.Text>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="role-box">
                      <Typography.Text className="text">
                        نقش کاربری:{" "}
                      </Typography.Text>
                      <Typography.Text className="text">
                        {user.roleName}
                      </Typography.Text>
                    </Col>
                  </Row>
                </div>
              )}
              {adminAccess && !isCollapsed && (
                <div
                  className="admin-btn-box"
                  onClick={() => {
                    if (currentSpace === "admin") {
                      dispatch(appSlice.actions.setCurrentSpace("user"));
                    } else {
                      dispatch(appSlice.actions.setCurrentSpace("admin"));
                    }
                  }}
                >
                  <Row
                    className="btn"
                    align={"middle"}
                    justify={"space-between"}
                  >
                    <Col
                      xs={4}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Icon src="/assets/icons/User - Circle.svg" />
                    </Col>
                    <Col xs={15}>
                      {currentSpace === "user" && (
                        <Typography.Text className="text">
                          پنل ادمین
                        </Typography.Text>
                      )}
                      {currentSpace === "admin" && (
                        <Typography.Text className="text">
                          پنل کاربری
                        </Typography.Text>
                      )}
                    </Col>
                    <Col
                      xs={4}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Icon src="/assets/icons/To-Left.svg" />
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          </div>
        </Sider>
        <Layout>
          <Header style={{ padding: "0 20px", backgroundColor: "#fff" }}>
            {(isCollapsed || screens.md) && (
              <Row
                justify={"space-between"}
                align={"middle"}
                style={{ height: "100%" }}
              >
                {screens.md &&
                  user.permissions.find(
                    (p) => p.code === PermissionCode.getSystemHealth
                  ) && (
                    <Col md={{ span: 4 }}>
                      {systemHealthStatus == 'true' && (
                        <Tag
                          icon={<CheckCircleOutlined />}
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontWeight: 400,
                            fontSize: 12,
                            padding: "5px 10px",
                          }}
                          color="success"
                        >
                          وضعیت سیستم: پایدار
                        </Tag>
                      )}
                      {systemHealthStatus == 'false' && (
                        <Tag
                          icon={<CloseCircleOutlined />}
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontWeight: 400,
                            fontSize: 12,
                            padding: "5px 10px",
                          }}
                          color="error"
                        >
                          وضعیت سیستم: ناپایدار
                        </Tag>
                      )}
                      {systemHealthStatus == 'license-false' && (
                        <Tag
                          icon={<CloseCircleOutlined />}
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontWeight: 400,
                            fontSize: 12,
                            padding: "5px 10px",
                          }}
                          color="error"
                        >
                          وضعیت سیستم: اتمام لایسنس
                        </Tag>
                      )}
                    </Col>
                  )}
                <Col xs={{ offset: 2, span: 22 }} md={{ offset: 12, span: 7 }}>
                  <Row align={"middle"} justify={"space-between"}>
                    <Col>
                      <div className="calendar-box">
                        <div className="time-box">
                          <Typography.Text type="secondary" className="time">
                            {time}
                          </Typography.Text>
                          <Divider type="vertical" />
                          <Typography.Text className="date">
                            {date}
                          </Typography.Text>
                        </div>
                      </div>
                    </Col>
                    <Col>
                      <Dropdown
                        trigger={["hover", "click"]}
                        menu={{
                          items: [
                            {
                              key: "1",
                              label: "پروفایل",
                              onClick: () => {
                                navigate("/panel/profile");
                              },
                              icon: (
                                <i
                                  style={{ fontSize: 20 }}
                                  className="las la-user"
                                ></i>
                              ),
                            },
                            {
                              icon: (
                                <i
                                  style={{ fontSize: 20 }}
                                  className="las la-sign-out-alt"
                                ></i>
                              ),
                              key: "2",
                              label: "خروج",
                              onClick: () => {
                                logout();
                              },
                              style: {
                                color: "#f00",
                              },
                            },
                          ],
                        }}
                      >
                        <div className="profile-box">
                          <Typography.Text>
                            {user.first_name} {user.last_name}
                          </Typography.Text>
                          <Gravatar
                            default="mp"
                            className="image"
                            rating="pg"
                            email={user.email ? user.email : "admin@test.com"}
                            size={35}
                          />
                        </div>
                      </Dropdown>
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}
          </Header>
          {(isCollapsed || screens.md) && (
            <Content
              style={{
                margin: "0 16px",
                padding: screens.xs ? "50px 5px" : "50px 30px",
                overflow: "auto",
                height: "90vh",
              }}
            >
              <Routes>
                <Route
                  path="/"
                  element={<Navigate to={"/panel/dashboard"} />}
                />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/report" element={<Report />} />
                <Route path="/export" element={<Export />} />
                <Route path="/profile" element={<Profile />} />

                <Route
                  path="/admin/org/profile"
                  element={<OrganizationProfile />}
                />
                <Route
                  path="/admin/org/settings"
                  element={<OrganizationSetting />}
                />
                <Route path="/admin/org/roles" element={<OrganizationRole />} />
                <Route path="/admin/org/users" element={<Users />} />
                <Route path="/admin/log-setting" element={<LogSetting />} />
                <Route path="/admin/app-management" element={<Application />} />
                <Route path="/admin/org/risk-management" element={<Risk />} />
                <Route path="/admin/alert/rule" element={<AlertRule />} />
                <Route
                  path="/admin/alert/receiver"
                  element={<AlertReceiver />}
                />
              </Routes>
            </Content>
          )}
        </Layout>
      </Layout>
    </div>
  );
};

export default Panel;
